import React, { useState } from 'react';
import { Box, Typography, Dialog, CircularProgress } from '@material-ui/core';
import { DateTime } from 'luxon';
import { TabletButton } from '@campfire/tablet-button';
import { gql } from '@apollo/client';
import Icon from '@mdi/react';
import { useCampfireQuery } from '../../../../global/network/useCampfireQuery';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { iconList } from '../../../../common/icons/RoleIconList';
import { ChatRosterCard } from './ChatRosterCard';
import { ChatCICOCard } from './ChatCICOCard';

const GET_ACTIVITY_FOR_CARD = gql`
  query GetActivityForChatCard($activityId: String!) {
    vm {
      activity(activityId: $activityId) {
        activityId
        name
        startDate
        endDate
        startTime
        endTime
        isEvent
        priority
        __typename
        ... on VOLUNTEER_RecurringActivityType {
          schedule {
            recurrences {
              humanReadable
            }
          }
        }
        activityLocation {
          placesAddress {
            description
          }
        }
        activityRemoteLocation {
          details
        }
        program {
          name
        }
      }
    }
  }
`;

const GET_REPORT_FOR_CARD = gql`
  query GetReportForChatCard($activityId: String!, $activityDate: Date!) {
    vm {
      activity(activityId: $activityId) {
        activityId
        name
        sessions(includeOneOff: true) {
          sessionId
        }
        program {
          name
        }
      }
      activityReport(activityId: $activityId, activityDate: $activityDate) {
        activityReportId
        sessionReports {
          sessionReportId
        }
        cancelledSessions {
          sessionId
        }
      }
    }
  }
`;

const GET_RESOURCE_FOR_CARD = gql`
  query GetResourceForChatCard($resourceId: String!) {
    vm {
      supportResource(supportResourceId: $resourceId) {
        supportResourceId
        title
        supportCategory {
          supportCategoryId
          title
          icon
          colour
        }
      }
    }
  }
`;

interface MessageMetadata {
  type?: string;
  [key: string]: unknown;
}

interface RichMessageCardProps {
  metadata: MessageMetadata;
  isOwn: boolean;
  messageId?: string;
  conversationId?: string;
  onVote?: (messageId: string, option: string) => void;
}

const CardWrapper = ({ children, isOwn }: { children: React.ReactNode; isOwn: boolean }) => (
  <Box
    style={{
      background: isOwn ? 'rgba(255,255,255,0.15)' : '#fff',
      border: isOwn ? '1px solid rgba(255,255,255,0.3)' : '1px solid #e0e0e0',
      borderRadius: 12,
      padding: 12,
      minWidth: 220,
      maxWidth: 320,
    }}
  >
    {children}
  </Box>
);

const TypeBadge = ({ label, isOwn }: { label: string; isOwn: boolean }) => (
  <Typography
    variant='caption'
    style={{
      fontWeight: 600,
      fontSize: '0.65rem',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      color: isOwn ? 'rgba(255,255,255,0.7)' : '#999',
      marginBottom: 4,
      display: 'block',
    }}
  >
    {label}
  </Typography>
);

const PollCard = ({ metadata, isOwn, messageId, onVote }: RichMessageCardProps) => {
  const question = metadata.question as string;
  const options = (metadata.options as string[]) || [];
  const votes = (metadata.votes as Record<string, string[]>) || {};
  const totalVotes = Object.values(votes).reduce((sum, arr) => sum + arr.length, 0);

  return (
    <CardWrapper isOwn={isOwn}>
      <TypeBadge label='Poll' isOwn={isOwn} />
      <Typography style={{ fontWeight: 600, fontSize: '0.9rem', color: isOwn ? '#fff' : '#333', marginBottom: 8 }}>
        {question}
      </Typography>
      {options.map((option) => {
        const voteCount = (votes[option] || []).length;
        const percentage = totalVotes > 0 ? Math.round((voteCount / totalVotes) * 100) : 0;
        return (
          <Box
            key={option}
            onClick={() => onVote && messageId && onVote(messageId, option)}
            style={{
              background: isOwn ? 'rgba(255,255,255,0.1)' : '#f5f5f5',
              borderRadius: 6,
              padding: '6px 10px',
              marginBottom: 4,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: onVote ? 'pointer' : 'default',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {totalVotes > 0 && (
              <Box
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: `${percentage}%`,
                  background: isOwn ? 'rgba(255,255,255,0.15)' : 'rgba(124, 77, 255, 0.1)',
                  transition: 'width 0.3s ease',
                }}
              />
            )}
            <Typography variant='body2' style={{ color: isOwn ? '#fff' : '#333', position: 'relative' }}>
              {option}
            </Typography>
            <Typography
              variant='caption'
              style={{ color: isOwn ? 'rgba(255,255,255,0.7)' : '#999', position: 'relative', fontWeight: 600 }}
            >
              {voteCount > 0 ? `${voteCount} (${percentage}%)` : ''}
            </Typography>
          </Box>
        );
      })}
      {totalVotes > 0 && (
        <Typography
          variant='caption'
          style={{ color: isOwn ? 'rgba(255,255,255,0.5)' : '#bbb', marginTop: 4, display: 'block' }}
        >
          {totalVotes} {totalVotes === 1 ? 'vote' : 'votes'}
        </Typography>
      )}
    </CardWrapper>
  );
};

const ActivityLinkCard = ({ metadata }: RichMessageCardProps) => {
  const { data, loading } = useCampfireQuery<any, any>(GET_ACTIVITY_FOR_CARD, {
    options: { variables: { activityId: metadata.activityId as string } },
  });

  const activity = data?.vm?.activity;

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' p={1}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (!activity) {
    return (
      <Typography variant='body2' style={{ color: '#999', padding: 8 }}>
        Activity no longer available
      </Typography>
    );
  }

  const recurrence =
    activity.__typename === 'VOLUNTEER_RecurringActivityType'
      ? activity.schedule?.recurrences?.[0]?.humanReadable
      : activity.isEvent
      ? 'Event'
      : 'Flexible';

  const formatTime = (time: string) => {
    const [h, m] = time.split(':');
    const hour = parseInt(h, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${m} ${ampm}`;
  };

  const location = activity.activityLocation?.placesAddress?.description || activity.activityRemoteLocation?.details;
  const grey = '#9E9E9E';

  let dateLabel = '';
  if (activity.startDate || activity.endDate) {
    if (activity.isEvent && activity.startDate && activity.endDate) {
      const s = DateTime.fromISO(activity.startDate);
      const e = DateTime.fromISO(activity.endDate);
      if (s.toISODate() === e.toISODate()) dateLabel = s.toFormat('d LLLL, y');
      else if (s.year === e.year && s.month === e.month) dateLabel = `${s.toFormat('d')} – ${e.toFormat('d LLLL, y')}`;
      else if (s.year === e.year) dateLabel = `${s.toFormat('d LLL')} – ${e.toFormat('d LLL, y')}`;
      else dateLabel = `${s.toFormat('d LLL y')} – ${e.toFormat('d LLL y')}`;
    } else if (activity.startDate) {
      const diffNow = DateTime.fromISO(activity.startDate)
        .diffNow()
        .as('days');
      if (diffNow > 0) dateLabel = `Starts ${DateTime.fromISO(activity.startDate).toFormat('dd LLLL, y')}`;
      else if (activity.endDate) dateLabel = `Ends ${DateTime.fromISO(activity.endDate).toFormat('dd LLLL, y')}`;
    }
  }

  return (
    <Box
      style={{ width: 480, cursor: 'pointer', borderRadius: 8, border: '1px solid #efefef', overflow: 'hidden' }}
      onClick={() => {
        window.location.assign(`/volunteering/activities?tab=all&activityId=${metadata.activityId}`);
      }}
    >
      <Box display='flex' overflow='hidden'>
        <Box style={{ width: 18, minWidth: 18, background: '#e8e8e8', borderRadius: '8px 0 0 8px' }} />
        <Box style={{ padding: '14px 18px', flex: 1, background: '#fff' }}>
          <Typography style={{ fontWeight: 800, fontSize: '1rem', color: '#545454', letterSpacing: '-0.02em' }} noWrap>
            {activity.name}
          </Typography>

          <Box display='flex' alignItems='center' mt={0.5}>
            {activity.isEvent ? (
              <svg
                width='14'
                height='14'
                viewBox='0 0 24 24'
                fill='none'
                stroke={grey}
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
                style={{ marginRight: 6, flexShrink: 0 }}
              >
                <path d='M4 5m0 2a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2z' />
                <path d='M16 3v4' />
                <path d='M8 3v4' />
                <path d='M4 11h16' />
              </svg>
            ) : (
              <svg
                width='14'
                height='14'
                viewBox='0 0 24 24'
                fill='none'
                stroke={grey}
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
                style={{ marginRight: 6, flexShrink: 0 }}
              >
                <path d='M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4' />
                <path d='M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4' />
              </svg>
            )}
            <Typography variant='caption' style={{ color: grey }}>
              {recurrence}
              {!activity.isEvent && activity.startTime && activity.endTime
                ? `, ${formatTime(activity.startTime)} - ${formatTime(activity.endTime)}`
                : ''}
            </Typography>
          </Box>

          <Box display='flex' flexWrap='wrap' alignItems='center' mt={1.5} style={{ gap: '8px' }}>
            {dateLabel && (
              <Box display='flex' alignItems='center'>
                <svg
                  width='14'
                  height='14'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke={grey}
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  style={{ marginRight: 6 }}
                >
                  <path d='M4 5m0 2a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2z' />
                  <path d='M16 3v4' />
                  <path d='M8 3v4' />
                  <path d='M4 11h16' />
                </svg>
                <Typography variant='caption' style={{ color: grey, marginTop: 1 }}>
                  {dateLabel}
                </Typography>
              </Box>
            )}
            {location && (
              <Box display='flex' alignItems='center'>
                <svg
                  width='14'
                  height='14'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke={grey}
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  style={{ marginRight: 6 }}
                >
                  <path d='M12 11m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0' />
                  <path d='M17.657 16.657l-4.243 4.243a2 2 0 0 1 -2.827 0l-4.244 -4.243a8 8 0 1 1 11.314 0z' />
                </svg>
                <Typography variant='caption' style={{ color: grey, marginTop: 1 }} noWrap>
                  {location}
                </Typography>
              </Box>
            )}
            <Box display='flex' alignItems='center'>
              <svg
                width='14'
                height='14'
                viewBox='0 0 24 24'
                fill='none'
                stroke={grey}
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
                style={{ marginRight: 6 }}
              >
                <path d='M8 16l2 -6l6 -2l-2 6l-6 2' />
                <path d='M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0' />
              </svg>
              <Typography variant='caption' style={{ color: grey, marginTop: 1 }}>
                {activity.program?.name}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box
          display='flex'
          alignItems='center'
          justifyContent='center'
          style={{ width: 36, minWidth: 36, background: '#f5f5f5', borderLeft: '1px solid #efefef' }}
        >
          <svg
            width='20'
            height='20'
            viewBox='0 0 24 24'
            fill='none'
            stroke='#9E9E9E'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <path d='M9 6l6 6l-6 6' />
          </svg>
        </Box>
      </Box>
    </Box>
  );
};

const LocationCard = ({ metadata, isOwn }: RichMessageCardProps) => (
  <CardWrapper isOwn={isOwn}>
    <TypeBadge label='Location' isOwn={isOwn} />
    {metadata.name && (
      <Typography style={{ fontWeight: 600, fontSize: '0.9rem', color: isOwn ? '#fff' : '#333' }}>
        {metadata.name as string}
      </Typography>
    )}
    <Typography variant='body2' style={{ color: isOwn ? 'rgba(255,255,255,0.9)' : '#555' }}>
      {metadata.address as string}
    </Typography>
    <Box mt={1}>
      <TabletButton
        variant={isOwn ? 'outlined' : 'contained'}
        color='primary'
        size='small'
        style={isOwn ? { borderColor: 'rgba(255,255,255,0.5)', color: '#fff' } : {}}
        onClick={() =>
          window.open(`https://maps.google.com/?q=${encodeURIComponent(metadata.address as string)}`, '_blank')
        }
      >
        Open in Maps
      </TabletButton>
    </Box>
  </CardWrapper>
);

const ImpactCard = ({ metadata, isOwn }: RichMessageCardProps) => (
  <CardWrapper isOwn={isOwn}>
    <TypeBadge label='Impact' isOwn={isOwn} />
    <Typography style={{ fontWeight: 700, fontSize: '1.5rem', color: isOwn ? '#fff' : '#7c4dff' }}>
      {metadata.value as string}
    </Typography>
    <Typography style={{ fontWeight: 500, fontSize: '0.9rem', color: isOwn ? 'rgba(255,255,255,0.9)' : '#333' }}>
      {metadata.metric as string}
    </Typography>
    {metadata.description && (
      <Typography variant='body2' style={{ color: isOwn ? 'rgba(255,255,255,0.7)' : '#999', marginTop: 4 }}>
        {metadata.description as string}
      </Typography>
    )}
  </CardWrapper>
);

const RosterLinkCard = ({ metadata }: RichMessageCardProps) => (
  <ChatRosterCard activityId={metadata.activityId as string} activityDate={metadata.activityDate as string} />
);

const CICOLinkCard = ({ metadata }: RichMessageCardProps) => (
  <ChatCICOCard activityId={metadata.activityId as string} activityDate={metadata.activityDate as string} />
);

const ResourceLinkCard = ({ metadata }: RichMessageCardProps) => {
  const { data, loading } = useCampfireQuery<any, any>(GET_RESOURCE_FOR_CARD, {
    options: { variables: { resourceId: metadata.resourceId as string } },
  });

  const resource = data?.vm?.supportResource;
  const grey = '#9E9E9E';

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' p={1}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (!resource) {
    return (
      <Box style={{ padding: 12, border: '1px solid #efefef', borderRadius: 8 }}>
        <Typography variant='body2' style={{ color: '#999' }}>
          Resource no longer available
        </Typography>
      </Box>
    );
  }

  const catColour = resource.supportCategory?.colour || '#7047EB';
  const catIcon = resource.supportCategory?.icon;
  const hasCatIcon = catIcon && (iconList as any)[catIcon];

  return (
    <Box
      style={{ width: 480, cursor: 'pointer', borderRadius: 8, border: '1px solid #efefef', overflow: 'hidden' }}
      onClick={() => {
        window.location.assign(
          `/warmth/resources/${resource.supportCategory?.supportCategoryId}/${resource.supportResourceId}`
        );
      }}
    >
      <Box display='flex' overflow='hidden'>
        <Box
          display='flex'
          flexDirection='column'
          alignItems='center'
          justifyContent='center'
          style={{ width: 80, minWidth: 80, background: `${catColour}18`, borderRadius: '8px 0 0 8px' }}
        >
          {hasCatIcon ? (
            <Icon path={(iconList as any)[catIcon]} size={1.3} color={catColour} />
          ) : (
            <Box style={{ width: 6, height: 32, borderRadius: 3, background: catColour }} />
          )}
        </Box>
        <Box style={{ padding: '14px 18px', flex: 1, background: '#fff' }}>
          <Typography style={{ fontWeight: 800, fontSize: '1rem', color: '#545454', letterSpacing: '-0.02em' }} noWrap>
            {resource.title}
          </Typography>
          <Box display='flex' alignItems='center' mt={0.5}>
            <Typography variant='caption' style={{ color: grey }}>
              {resource.supportCategory?.title}
            </Typography>
          </Box>
        </Box>
        <Box
          display='flex'
          alignItems='center'
          justifyContent='center'
          style={{ width: 36, minWidth: 36, background: '#f5f5f5', borderLeft: '1px solid #efefef' }}
        >
          <svg
            width='20'
            height='20'
            viewBox='0 0 24 24'
            fill='none'
            stroke='#9E9E9E'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <path d='M9 6l6 6l-6 6' />
          </svg>
        </Box>
      </Box>
    </Box>
  );
};

const ReportShareCard = ({ metadata }: RichMessageCardProps) => {
  const activityId = metadata.activityId as string;
  const activityDate = metadata.activityDate as string;
  const { theme } = useCampfireTheme();

  const { data, loading } = useCampfireQuery<any, any>(GET_REPORT_FOR_CARD, {
    options: { variables: { activityId, activityDate } },
  });

  const activity = data?.vm?.activity;
  const report = data?.vm?.activityReport;
  const grey = '#9E9E9E';
  const rosteredColors = (theme.color.rosters.status as any).rostered;

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' p={1}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (!activity) {
    return (
      <Box style={{ padding: 12, border: '1px solid #efefef', borderRadius: 8 }}>
        <Typography variant='body2' style={{ color: '#999' }}>
          Activity no longer available
        </Typography>
      </Box>
    );
  }

  const dt = DateTime.fromISO(activityDate);
  const totalSessions = activity.sessions?.length || 0;
  const completedSessions = report?.sessionReports?.length || 0;
  const cancelledSessions = report?.cancelledSessions?.length || 0;

  let statusLabel = 'Incomplete';
  let statusColour = '#EF5350';
  let panelBg = '#EF5350';
  let dayColour = '#fff';
  let monthColour = 'rgba(255,255,255,0.8)';

  if (completedSessions + cancelledSessions >= totalSessions && totalSessions > 0) {
    if (cancelledSessions === totalSessions) {
      statusLabel = 'Omitted';
      statusColour = grey;
      panelBg = '#F2F2F2';
      dayColour = '#646464';
      monthColour = grey;
    } else {
      statusLabel = 'Complete';
      statusColour = rosteredColors.secondary;
      panelBg = rosteredColors.primary;
      dayColour = '#fff';
      monthColour = rosteredColors.secondary;
    }
  } else if (!report) {
    statusLabel = 'Not Started';
    statusColour = grey;
    panelBg = '#F2F2F2';
    dayColour = '#646464';
    monthColour = grey;
  }

  return (
    <Box
      style={{ width: 480, cursor: 'pointer', borderRadius: 8, border: '1px solid #efefef', overflow: 'hidden' }}
      onClick={() => {
        window.location.assign(`/volunteering/activity-report?activityId=${activityId}&activityDate=${activityDate}`);
      }}
    >
      <Box display='flex' overflow='hidden'>
        <Box
          display='flex'
          flexDirection='column'
          alignItems='center'
          justifyContent='center'
          style={{ width: 80, minWidth: 80, background: panelBg, borderRadius: '8px 0 0 8px' }}
        >
          <Typography style={{ fontWeight: 900, fontSize: 30, lineHeight: 1, color: dayColour }}>{dt.day}</Typography>
          <Typography style={{ fontSize: 14, fontWeight: 700, color: monthColour, marginTop: 2 }}>
            {dt.monthShort}
          </Typography>
        </Box>
        <Box style={{ padding: '14px 18px', flex: 1, background: '#fff' }}>
          <Typography style={{ fontWeight: 800, fontSize: '1rem', color: '#545454', letterSpacing: '-0.02em' }} noWrap>
            {activity.name}
          </Typography>
          <Box display='flex' flexWrap='wrap' alignItems='center' mt={0.5} style={{ gap: '8px' }}>
            <Box display='flex' alignItems='center'>
              <svg
                width='14'
                height='14'
                viewBox='0 0 24 24'
                fill='none'
                stroke={grey}
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
                style={{ marginRight: 6 }}
              >
                <path d='M8 16l2 -6l6 -2l-2 6l-6 2' />
                <path d='M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0' />
              </svg>
              <Typography variant='caption' style={{ color: grey }}>
                {activity.program?.name}
              </Typography>
            </Box>
            <Typography variant='caption' style={{ color: statusColour, fontWeight: 600 }}>
              {statusLabel}
            </Typography>
          </Box>
        </Box>
        <Box
          display='flex'
          alignItems='center'
          justifyContent='center'
          style={{ width: 36, minWidth: 36, background: '#f5f5f5', borderLeft: '1px solid #efefef' }}
        >
          <svg
            width='20'
            height='20'
            viewBox='0 0 24 24'
            fill='none'
            stroke='#9E9E9E'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <path d='M9 6l6 6l-6 6' />
          </svg>
        </Box>
      </Box>
    </Box>
  );
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FileCard = ({ metadata, isOwn }: RichMessageCardProps) => (
  <CardWrapper isOwn={isOwn}>
    <Box display='flex' alignItems='center' style={{ gap: 10 }}>
      <svg
        width='24'
        height='24'
        viewBox='0 0 24 24'
        fill='none'
        stroke={isOwn ? '#fff' : '#7c4dff'}
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      >
        <path d='M14 3v4a1 1 0 0 0 1 1h4' />
        <path d='M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z' />
      </svg>
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant='body2'
          style={{
            fontWeight: 600,
            color: isOwn ? '#fff' : '#333',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {metadata.fileName as string}
        </Typography>
        {metadata.fileSize && (
          <Typography variant='caption' style={{ color: isOwn ? 'rgba(255,255,255,0.7)' : '#999' }}>
            {formatFileSize(Number(metadata.fileSize))}
          </Typography>
        )}
      </Box>
    </Box>
    <Box mt={1}>
      <TabletButton
        variant={isOwn ? 'outlined' : 'contained'}
        color='primary'
        size='small'
        style={isOwn ? { borderColor: 'rgba(255,255,255,0.5)', color: '#fff' } : {}}
        onClick={() => window.open(metadata.fileUrl as string, '_blank')}
      >
        Download
      </TabletButton>
    </Box>
  </CardWrapper>
);

const PhotoCard = ({ metadata }: { metadata: MessageMetadata }) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <>
      <Box style={{ cursor: 'pointer', maxWidth: 300 }} onClick={() => setDialogOpen(true)}>
        <img
          src={metadata.imageUrl as string}
          alt='Shared photo'
          style={{ maxWidth: '100%', borderRadius: 8, display: 'block' }}
        />
      </Box>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth='lg'>
        <img src={metadata.imageUrl as string} alt='Shared photo' style={{ width: '100%', display: 'block' }} />
      </Dialog>
    </>
  );
};

export const RichMessageCard = ({ metadata, isOwn, messageId, onVote }: RichMessageCardProps) => {
  if (!metadata?.type) return null;

  switch (metadata.type) {
    case 'poll':
      return <PollCard metadata={metadata} isOwn={isOwn} messageId={messageId} onVote={onVote} />;
    case 'activity_link':
      return <ActivityLinkCard metadata={metadata} isOwn={isOwn} />;
    case 'location_share':
      return <LocationCard metadata={metadata} isOwn={isOwn} />;
    case 'impact_share':
      return <ImpactCard metadata={metadata} isOwn={isOwn} />;
    case 'checkin_link':
    case 'cico_link':
      return <CICOLinkCard metadata={metadata} />;
    case 'roster_link':
      return <RosterLinkCard metadata={metadata} isOwn={isOwn} />;
    case 'resource_link':
      return <ResourceLinkCard metadata={metadata} isOwn={isOwn} />;
    case 'report_share':
      return <ReportShareCard metadata={metadata} isOwn={isOwn} />;
    case 'photo':
      return <PhotoCard metadata={metadata} />;
    case 'file':
      return <FileCard metadata={metadata} isOwn={isOwn} />;
    default:
      return null;
  }
};
